import React from 'react';
import styled, { ThemeProvider } from 'styled-components';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import theme from './styles/theme';
import GlobalStyles from './styles/globalStyles';
import LoginPage from './components/auth/LoginPage';
import SignupPage from './components/auth/SignupPage';
import VideoPlayer from './components/video/VideoPlayer';
import SubscriptionPlans from './components/billing/SubscriptionPlans';
import BillingPortal from './components/billing/BillingPortal';
import UpgradeModal from './components/billing/UpgradeModal';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { subscriptionService } from './services/subscriptionService';

const AppContainer = styled.div`
  height: 100vh;
  width: 100vw;
  overflow: hidden;
`;

const ScrollableContainer = styled.div`
  height: 100vh;
  width: 100vw;
  overflow-y: auto;
`;

// Protected route wrapper - requires authentication
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <AppContainer>
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          color: theme.colors.text
        }}>
          Loading...
        </div>
      </AppContainer>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Subscription-protected route - requires active subscription to use main features
const SubscriptionRoute = ({ children }) => {
  const { user, subscription, loading } = useAuth();
  const navigate = useNavigate();

  // Still loading auth AND subscription data
  if (loading) {
    return (
      <AppContainer>
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          color: theme.colors.text
        }}>
          Loading...
        </div>
      </AppContainer>
    );
  }

  // Not logged in
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Subscription not loaded or error occurred
  if (!subscription) {
    return (
      <AppContainer>
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          color: theme.colors.text,
          flexDirection: 'column',
          gap: '16px'
        }}>
          <div>Loading subscription...</div>
          <div style={{ fontSize: '12px', color: theme.colors.textSecondary }}>
            If this persists, check the browser console for errors.
          </div>
        </div>
      </AppContainer>
    );
  }

  // Subscription fetch had an error - show error and redirect to plans
  if (subscription.status === 'error') {
    return (
      <AppContainer>
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          color: theme.colors.text,
          flexDirection: 'column',
          gap: '16px',
          padding: '20px',
          textAlign: 'center'
        }}>
          <div style={{ color: '#ff6b6b' }}>Error loading subscription</div>
          <div style={{ fontSize: '14px', color: theme.colors.textSecondary, maxWidth: '400px' }}>
            {subscription.error || 'Unknown error occurred'}
          </div>
          <button
            onClick={() => navigate('/plans')}
            style={{
              padding: '10px 20px',
              background: theme.colors.primary,
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              marginTop: '10px'
            }}
          >
            View Plans
          </button>
        </div>
      </AppContainer>
    );
  }

  // Check if subscription allows usage
  if (!subscription.canUseService) {
    const reason = subscriptionService.getUpgradeReason(subscription);
    const modalReason = reason === 'trial_expired' ? 'trial_expired' :
      reason === 'limit_reached' ? 'limit' :
        reason === 'subscription_expired' ? 'subscription_expired' :
          'default';

    return (
      <>
        <AppContainer>
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
            color: theme.colors.textSecondary,
            flexDirection: 'column',
            gap: '16px'
          }}>
            <div>Please subscribe to continue</div>
          </div>
        </AppContainer>
        <UpgradeModal
          isOpen={true}
          onClose={() => navigate('/plans')}
          reason={modalReason}
        />
      </>
    );
  }

  // Subscription is active - allow access
  return children;
};

// Public route wrapper - redirects to home if already logged in
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <AppContainer>
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          color: theme.colors.text
        }}>
          Loading...
        </div>
      </AppContainer>
    );
  }

  if (user) {
    return <Navigate to="/" replace />;
  }

  return children;
};

const AppContent = () => {
  return (
    <Routes>
      {/* Public routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      <Route
        path="/signup"
        element={
          <PublicRoute>
            <SignupPage />
          </PublicRoute>
        }
      />

      {/* Protected routes - require active subscription */}
      <Route
        path="/"
        element={
          <SubscriptionRoute>
            <AppContainer>
              <VideoPlayer />
            </AppContainer>
          </SubscriptionRoute>
        }
      />

      {/* Protected routes - don't require subscription (billing/plan pages) */}
      <Route
        path="/plans"
        element={
          <ProtectedRoute>
            <ScrollableContainer>
              <SubscriptionPlans onPlanSelected={() => window.location.href = '/'} />
            </ScrollableContainer>
          </ProtectedRoute>
        }
      />
      <Route
        path="/billing"
        element={
          <ProtectedRoute>
            <ScrollableContainer>
              <BillingPortal onShowPlans={() => window.location.href = '/plans'} isFullPage />
            </ScrollableContainer>
          </ProtectedRoute>
        }
      />

      {/* Catch all - redirect to home or login */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

const App = () => {
  return (
    <ThemeProvider theme={theme}>
      <GlobalStyles isPaused={false} />
      <Router>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </Router>
    </ThemeProvider>
  );
};

export default App;
